// Compact, names-first opening courses. These deliberately complement the
// hand-authored guided courses: the moves and variation names are curated,
// while detailed strategic prose is left for the guided tier.
//
// Names and canonical PGNs follow the pinned CC0 lichess-org/chess-openings
// corpus used by openingBook.json. The list is intentionally an allowlist of
// established family/main-system choices; novelty gambits and joke sidelines
// from the much larger naming corpus do not become supported answers here.

import { Chess } from "chessops/chess";
import { parseSan } from "chessops/san";
import { makeUci } from "chessops/util";

import type { CourseGroup, CourseLine, L10n, MoveSpec, OpeningCourse } from "./openingCourses.ts";
import type { Color } from "../game/types.ts";

const L = (en: string, sl = en): L10n => ({ en, sl });

interface CanonicalLine {
  id: string;
  /** Full standard name, including the opening family and variation. */
  name: string;
  pgn: string;
}

interface SupportedCourseSpec {
  slug: string;
  eco: string;
  name: L10n;
  group: CourseGroup;
  color: Color;
  lines: CanonicalLine[];
}

function pgnMoves(pgn: string): MoveSpec[] {
  const position = Chess.default();
  return pgn
    .replace(/\d+\.(\.\.)?/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((san) => {
      const move = parseSan(position, san);
      if (!move) throw new Error(`invalid supported opening PGN at ${san}: ${pgn}`);
      const uci = makeUci(move);
      position.play(move);
      return { uci, san };
    });
}

function sharedPlies(a: readonly MoveSpec[], b: readonly MoveSpec[]): number {
  let at = 0;
  while (at < a.length && at < b.length && a[at].uci === b[at].uci) at++;
  return at;
}

function supportedCourse(spec: SupportedCourseSpec): OpeningCourse {
  const resolved = spec.lines.map((line) => ({ ...line, moves: pgnMoves(line.pgn) }));
  const main = resolved[0];
  if (!main) throw new Error(`${spec.slug}: supported course has no lines`);
  const lines: CourseLine[] = resolved.map((line, index) => {
    if (index === 0) return { id: line.id, name: L(line.name), moves: line.moves };
    const at = sharedPlies(main.moves, line.moves);
    if (at >= line.moves.length) throw new Error(`${spec.slug}/${line.id}: line does not diverge from main`);
    return { id: line.id, at, name: L(line.name), moves: line.moves.slice(at) };
  });
  return {
    ...spec,
    kind: "supported",
    tagline: L("Supported theory · standard moves and names", "Podprta teorija · standardne poteze in imena"),
    intro: L(
      "A compact, names-first opening study. It covers established continuations without detailed move-by-move coaching.",
      "Zgoščena študija otvoritve s poudarkom na imenih. Zajema uveljavljena nadaljevanja brez podrobne razlage vsake poteze.",
    ),
    lines,
    summary: L(
      "You have rehearsed the supported main systems. Use the variation names as a map for deeper study and analysis.",
      "Ponovil si podprte glavne sisteme. Imena variant uporabi kot zemljevid za globljo študijo in analizo.",
    ),
  };
}

const SPECS: SupportedCourseSpec[] = [
  {
    slug: "petrov",
    eco: "C42–C43",
    name: L("Petrov's Defense", "Petrova obramba"),
    group: "open",
    color: "black",
    lines: [
      { id: "classical", name: "Petrov's Defense: Classical Attack", pgn: "1. e4 e5 2. Nf3 Nf6 3. Nxe5 d6 4. Nf3 Nxe4 5. d4" },
      { id: "modern", name: "Petrov's Defense: Modern Attack", pgn: "1. e4 e5 2. Nf3 Nf6 3. d4 exd4" },
      { id: "three-knights", name: "Petrov's Defense: Three Knights Game", pgn: "1. e4 e5 2. Nf3 Nf6 3. Nc3" },
      { id: "italian", name: "Petrov's Defense: Italian Variation", pgn: "1. e4 e5 2. Nf3 Nf6 3. Bc4" },
    ],
  },
  {
    slug: "scotch",
    eco: "C44–C45",
    name: L("Scotch Game", "Škotska otvoritev"),
    group: "open",
    color: "white",
    lines: [
      { id: "classical", name: "Scotch Game: Classical Variation", pgn: "1. e4 e5 2. Nf3 Nc6 3. d4 exd4 4. Nxd4 Bc5" },
      { id: "schmidt", name: "Scotch Game: Schmidt Variation", pgn: "1. e4 e5 2. Nf3 Nc6 3. d4 exd4 4. Nxd4 Nf6" },
      { id: "gambit", name: "Scotch Game: Scotch Gambit", pgn: "1. e4 e5 2. Nf3 Nc6 3. d4 exd4 4. Bc4" },
    ],
  },
  {
    slug: "vienna",
    eco: "C25–C29",
    name: L("Vienna Game", "Dunajska otvoritev"),
    group: "open",
    color: "white",
    lines: [
      { id: "gambit", name: "Vienna Game: Vienna Gambit, Main Line", pgn: "1. e4 e5 2. Nc3 Nf6 3. f4 d5" },
      { id: "stanley", name: "Vienna Game: Stanley Variation", pgn: "1. e4 e5 2. Nc3 Nf6 3. Bc4" },
      { id: "mieses", name: "Vienna Game: Mieses Variation", pgn: "1. e4 e5 2. Nc3 Nf6 3. g3" },
    ],
  },
  {
    slug: "kings-gambit",
    eco: "C30–C34",
    name: L("King's Gambit", "Kraljevi gambit"),
    group: "open",
    color: "white",
    lines: [
      { id: "knight", name: "King's Gambit Accepted: King's Knight's Gambit", pgn: "1. e4 e5 2. f4 exf4 3. Nf3" },
      { id: "bishop", name: "King's Gambit Accepted: Bishop's Gambit", pgn: "1. e4 e5 2. f4 exf4 3. Bc4" },
      { id: "falkbeer", name: "King's Gambit Declined: Falkbeer Countergambit", pgn: "1. e4 e5 2. f4 d5 3. exd5" },
      { id: "classical", name: "King's Gambit Declined: Classical Variation", pgn: "1. e4 e5 2. f4 Bc5" },
    ],
  },
  {
    slug: "four-knights",
    eco: "C47–C49",
    name: L("Four Knights Game", "Otvoritev štirih skakačev"),
    group: "open",
    color: "white",
    lines: [
      { id: "spanish", name: "Four Knights Game: Spanish Variation", pgn: "1. e4 e5 2. Nf3 Nc6 3. Nc3 Nf6 4. Bb5" },
      { id: "scotch", name: "Four Knights Game: Scotch Variation Accepted", pgn: "1. e4 e5 2. Nf3 Nc6 3. Nc3 Nf6 4. d4 exd4" },
      { id: "italian", name: "Four Knights Game: Italian Variation", pgn: "1. e4 e5 2. Nf3 Nc6 3. Bc4 Bc5 4. Nc3 Nf6" },
    ],
  },
  {
    slug: "philidor",
    eco: "C41",
    name: L("Philidor Defense", "Philidorjeva obramba"),
    group: "open",
    color: "black",
    lines: [
      { id: "exchange", name: "Philidor Defense: Exchange Variation", pgn: "1. e4 e5 2. Nf3 d6 3. d4 exd4 4. Nxd4 Nf6" },
      { id: "hanham", name: "Philidor Defense: Hanham Variation", pgn: "1. e4 e5 2. Nf3 d6 3. d4 Nd7" },
      { id: "lion", name: "Philidor Defense: Lion Variation", pgn: "1. e4 e5 2. Nf3 d6 3. d4 Nf6 4. Nc3 Nbd7" },
    ],
  },
  {
    slug: "alekhine",
    eco: "B02–B05",
    name: L("Alekhine Defense", "Aljehinova obramba"),
    group: "semi-open",
    color: "black",
    lines: [
      { id: "modern", name: "Alekhine Defense: Modern Variation, Main Line", pgn: "1. e4 Nf6 2. e5 Nd5 3. d4 d6 4. Nf3 Bg4" },
      { id: "exchange", name: "Alekhine Defense: Exchange Variation", pgn: "1. e4 Nf6 2. e5 Nd5 3. d4 d6 4. c4 Nb6 5. exd6" },
      { id: "four-pawns", name: "Alekhine Defense: Four Pawns Attack", pgn: "1. e4 Nf6 2. e5 Nd5 3. d4 d6 4. c4 Nb6 5. f4" },
    ],
  },
  {
    slug: "pirc",
    eco: "B07–B09",
    name: L("Pirc Defense", "Pircova obramba"),
    group: "semi-open",
    color: "black",
    lines: [
      { id: "classical", name: "Pirc Defense: Classical Variation, Quiet System", pgn: "1. e4 d6 2. d4 Nf6 3. Nc3 g6 4. Nf3 Bg7 5. Be2" },
      { id: "austrian", name: "Pirc Defense: Austrian Attack", pgn: "1. e4 d6 2. d4 Nf6 3. Nc3 g6 4. f4 Bg7 5. Nf3 O-O" },
      { id: "150-attack", name: "Pirc Defense: 150 Attack", pgn: "1. e4 d6 2. d4 Nf6 3. Nc3 g6 4. Be3 c6 5. Qd2" },
    ],
  },
  {
    slug: "modern",
    eco: "B06",
    name: L("Modern Defense", "Moderna obramba"),
    group: "semi-open",
    color: "black",
    lines: [
      { id: "standard", name: "Modern Defense: Standard Defense", pgn: "1. e4 g6 2. d4 Bg7 3. Nc3 d6" },
      { id: "two-knights", name: "Modern Defense: Two Knights Variation", pgn: "1. e4 g6 2. d4 Bg7 3. Nc3 d6 4. Nf3" },
      { id: "three-pawns", name: "Modern Defense: Three Pawns Attack", pgn: "1. e4 g6 2. d4 Bg7 3. f4" },
      { id: "bishop", name: "Modern Defense: Bishop Attack", pgn: "1. e4 g6 2. d4 Bg7 3. Bc4" },
    ],
  },
  {
    slug: "kings-indian",
    eco: "E60–E99",
    name: L("King's Indian Defense", "Kraljevoindijska obramba"),
    group: "closed-flank",
    color: "black",
    lines: [
      { id: "orthodox", name: "King's Indian Defense: Orthodox Variation", pgn: "1. d4 Nf6 2. c4 g6 3. Nc3 Bg7 4. e4 d6 5. Nf3 O-O 6. Be2" },
      { id: "samisch", name: "King's Indian Defense: Sämisch Variation, Normal Defense", pgn: "1. d4 Nf6 2. c4 g6 3. Nc3 Bg7 4. e4 d6 5. f3 O-O" },
      { id: "fianchetto", name: "King's Indian Defense: Fianchetto Variation", pgn: "1. d4 Nf6 2. c4 g6 3. Nf3 Bg7 4. g3" },
    ],
  },
  {
    slug: "grunfeld",
    eco: "D80–D99",
    name: L("Grünfeld Defense", "Grünfeldova obramba"),
    group: "closed-flank",
    color: "black",
    lines: [
      { id: "exchange", name: "Grünfeld Defense: Exchange Variation", pgn: "1. d4 Nf6 2. c4 g6 3. Nc3 d5 4. cxd5 Nxd5" },
      { id: "russian", name: "Grünfeld Defense: Russian Variation", pgn: "1. d4 Nf6 2. c4 g6 3. Nc3 d5 4. Nf3 Bg7 5. Qb3" },
      { id: "three-knights", name: "Grünfeld Defense: Three Knights Variation, Hungarian Attack", pgn: "1. d4 Nf6 2. c4 g6 3. Nc3 d5 4. Nf3 Bg7 5. Bf4" },
    ],
  },
  {
    slug: "dutch",
    eco: "A80–A99",
    name: L("Dutch Defense", "Nizozemska obramba"),
    group: "closed-flank",
    color: "black",
    lines: [
      { id: "leningrad", name: "Dutch Defense: Leningrad Variation", pgn: "1. d4 f5 2. c4 Nf6 3. g3 g6 4. Bg2 Bg7 5. Nf3" },
      { id: "classical", name: "Dutch Defense: Classical Variation", pgn: "1. d4 f5 2. c4 Nf6 3. g3 e6 4. Bg2 Be7 5. Nf3 O-O 6. O-O d6" },
      { id: "stonewall", name: "Dutch Defense: Stonewall Variation", pgn: "1. d4 f5 2. c4 Nf6 3. g3 e6 4. Bg2 Be7 5. Nf3 O-O 6. O-O d5" },
    ],
  },
  {
    slug: "modern-benoni",
    eco: "A60–A79",
    name: L("Modern Benoni", "Moderni Benoni"),
    group: "closed-flank",
    color: "black",
    lines: [
      { id: "classical", name: "Benoni Defense: Classical Variation, Main Line", pgn: "1. d4 Nf6 2. c4 c5 3. d5 e6 4. Nc3 exd5 5. cxd5 d6 6. e4 g6 7. Nf3 Bg7 8. Be2 O-O 9. O-O" },
      { id: "fianchetto", name: "Benoni Defense: Fianchetto Variation", pgn: "1. d4 Nf6 2. c4 e6 3. Nf3 c5 4. d5 exd5 5. cxd5 d6 6. Nc3 g6 7. g3" },
      { id: "four-pawns", name: "Benoni Defense: Four Pawns Attack", pgn: "1. d4 Nf6 2. c4 c5 3. d5 e6 4. Nc3 exd5 5. cxd5 d6 6. e4 g6 7. f4 Bg7 8. Nf3 O-O" },
    ],
  },
  {
    slug: "benko",
    eco: "A57–A59",
    name: L("Benko Gambit", "Benkov gambit"),
    group: "closed-flank",
    color: "black",
    lines: [
      { id: "fianchetto", name: "Benko Gambit Accepted: Fianchetto Variation", pgn: "1. d4 Nf6 2. c4 c5 3. d5 b5 4. cxb5 a6 5. bxa6 g6 6. Nc3 Bxa6 7. g3 d6 8. Bg2 Bg7 9. Nf3" },
      { id: "modern", name: "Benko Gambit Accepted: Modern Variation", pgn: "1. d4 Nf6 2. c4 c5 3. d5 b5 4. cxb5 a6 5. e3" },
      { id: "declined", name: "Benko Gambit Declined: Main Line", pgn: "1. d4 Nf6 2. c4 c5 3. d5 b5 4. Nf3" },
    ],
  },
  {
    slug: "queens-indian",
    eco: "E12–E19",
    name: L("Queen's Indian Defense", "Damskoindijska obramba"),
    group: "closed-flank",
    color: "black",
    lines: [
      { id: "fianchetto", name: "Queen's Indian Defense: Fianchetto Variation, Traditional Line", pgn: "1. d4 Nf6 2. c4 e6 3. Nf3 b6 4. g3 Bb7" },
      { id: "kasparov-petrosian", name: "Queen's Indian Defense: Kasparov-Petrosian Variation, Main Line", pgn: "1. d4 Nf6 2. c4 e6 3. Nf3 b6 4. a3 Bb7 5. Nc3 d5" },
      { id: "spassky", name: "Queen's Indian Defense: Spassky System", pgn: "1. d4 Nf6 2. c4 e6 3. Nf3 b6 4. e3 Bb7 5. Bd3" },
    ],
  },
  {
    slug: "bogo-indian",
    eco: "E11",
    name: L("Bogo-Indian Defense", "Bogoindijska obramba"),
    group: "closed-flank",
    color: "black",
    lines: [
      { id: "nimzowitsch", name: "Bogo-Indian Defense: Nimzowitsch Variation", pgn: "1. d4 Nf6 2. c4 e6 3. Nf3 Bb4+ 4. Bd2 Qe7" },
      { id: "exchange", name: "Bogo-Indian Defense: Exchange Variation", pgn: "1. d4 Nf6 2. c4 e6 3. Nf3 Bb4+ 4. Bd2 Bxd2+" },
      { id: "retreat", name: "Bogo-Indian Defense: Retreat Variation", pgn: "1. d4 Nf6 2. c4 e6 3. Nf3 Bb4+ 4. Bd2 Be7" },
    ],
  },
  {
    slug: "trompowsky",
    eco: "A45",
    name: L("Trompowsky Attack", "Napad Trompowskega"),
    group: "closed-flank",
    color: "white",
    lines: [
      { id: "classical", name: "Trompowsky Attack: Classical Defense, Big Center Variation", pgn: "1. d4 Nf6 2. Bg5 e6 3. e4" },
      { id: "edge", name: "Trompowsky Attack: Edge Variation", pgn: "1. d4 Nf6 2. Bg5 Ne4 3. Bh4" },
      { id: "poisoned-pawn", name: "Trompowsky Attack: Poisoned Pawn Variation", pgn: "1. d4 Nf6 2. Bg5 c5 3. d5 Qb6 4. Nc3" },
    ],
  },
  {
    slug: "semi-slav",
    eco: "D43–D49",
    name: L("Semi-Slav Defense", "Polslovanska obramba"),
    group: "closed-flank",
    color: "black",
    lines: [
      { id: "main", name: "Semi-Slav Defense: Main Line", pgn: "1. d4 d5 2. c4 c6 3. Nc3 Nf6 4. e3 e6 5. Nf3 Nbd7 6. Bd3" },
      { id: "botvinnik", name: "Semi-Slav Defense: Botvinnik Variation", pgn: "1. d4 d5 2. c4 c6 3. Nf3 Nf6 4. Nc3 e6 5. Bg5 dxc4 6. e4" },
      { id: "moscow", name: "Semi-Slav Defense: Moscow Variation", pgn: "1. d4 d5 2. c4 c6 3. Nf3 Nf6 4. Nc3 e6 5. Bg5 h6 6. Bxf6 Qxf6" },
    ],
  },
];

export const SUPPORTED_OPENING_COURSES: OpeningCourse[] = SPECS.map(supportedCourse);
